// frontend/src/components/DocumentViewer/FullscreenDocumentModal.tsx

import React, { useState, useEffect, useRef } from "react";
import Modal from "../ui/Modal";
import type { Document } from "../../services/api";
import PDFView, { PDFViewRef } from "./PDFView";
import ImageView, { ImageViewRef } from "./ImageView";
import { useAuth } from "../../contexts/AuthContext";

interface FullscreenDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document;
  documentUrl: string;
}

const FullscreenDocumentModal: React.FC<FullscreenDocumentModalProps> = React.memo(({
  isOpen,
  onClose,
  document,
  documentUrl,
}) => {
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  // Auth context for permissions
  const { user } = useAuth();
  const canDownload = user?.canDownload || false;
  
  // Refs to access viewer functions
  const pdfViewerRef = useRef<PDFViewRef>(null);
  const imageViewerRef = useRef<ImageViewRef>(null);

  useEffect(() => {
    if (isOpen && !hasBeenOpened) {
      setHasBeenOpened(true);
    }
    if (isOpen) {
      setIsLoading(true);
      setLoadingProgress(0);
    }
  }, [isOpen, hasBeenOpened]);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Loading progress simulation for better UX
  useEffect(() => {
    if (isOpen && isLoading) {
      setLoadingProgress(0);
      const interval = setInterval(() => {
        setLoadingProgress((prev: number) => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90; // Stop at 90%, complete when document actually loads
          }
          return prev + 15;
        });
      }, 150);

      return () => clearInterval(interval);
    }
  }, [isOpen, isLoading]);

  // Handle document load completion
  const handleDocumentLoad = () => {
    setLoadingProgress(100);
    setTimeout(() => setIsLoading(false), 200);
  };

  const handleDocumentError = () => {
    setIsLoading(false);
  };

  // Keyboard shortcuts for fullscreen mode
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Prevent default for handled shortcuts
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        case '=':
        case '+':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            if (document.type === 'pdf') {
              pdfViewerRef.current?.handleZoomIn();
            }
          }
          break;
        case '-':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            if (document.type === 'pdf') {
              pdfViewerRef.current?.handleZoomOut();
            }
          }
          break;
        case '0':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            if (document.type === 'pdf') {
              pdfViewerRef.current?.handleResetZoom();
            }
          }
          break;
        case 'r':
        case 'R':
          if (document.type === 'image') {
            event.preventDefault();
            imageViewerRef.current?.handleRotate();
          }
          break;
        case 'd':
        case 'D':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            if (document.type === 'pdf') {
              pdfViewerRef.current?.handleDownload();
            } else {
              imageViewerRef.current?.handleDownload();
            }
          }
          break;
      }
    };

    window.document.addEventListener('keydown', handleKeyDown);
    return () => window.document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, document.type]);

  // Only render content after first open to avoid initial load
  if (!hasBeenOpened) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={document.title}
      maxWidth="full"
      className="!p-0 !m-0"
    >
      <div className={`${
        isMobile ? 'h-full w-full' : 'h-[calc(100vh-8rem)] w-[calc(100vw-8rem)]'
      }`}>
        <div className={`flex flex-col h-full bg-white ${
          isMobile ? '' : 'rounded-lg shadow-xl'
        }`} style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent'
        }}>
          {/* Loading overlay with progress */}
          {isLoading && (
            <div className={`absolute inset-0 bg-white/95 flex items-center justify-center z-10 ${
              isMobile ? '' : 'rounded-lg'
            }`}>
              <div className="text-center space-y-4">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                <div className="space-y-2">
                  <p className="text-lg font-medium text-gray-900">Caricamento documento...</p>
                  <div className="w-64 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${loadingProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600">{loadingProgress}%</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex-1 min-h-0 overflow-hidden relative">
            {document.type === "pdf" ? (
              <PDFView
                ref={pdfViewerRef}
                url={documentUrl}
                onLoadError={handleDocumentError}
                onLoadComplete={handleDocumentLoad}
                showControls={true}
                isFullscreenMode={true}
                fitToWidth={true}
                canDownload={canDownload}
              />
            ) : (
              <ImageView
                ref={imageViewerRef}
                url={documentUrl}
                documentId={document.id}
                showControls={true}
                className="flex items-center justify-center py-4"
                isFullscreenMode={true}
                onLoadComplete={handleDocumentLoad}
                onLoadError={handleDocumentError}
                canDownload={canDownload}
              />
            )}
          </div>
          <div className="flex-shrink-0 h-12 bg-white border-t border-gray-200 flex items-center justify-between px-4">
            <span className="text-sm text-gray-500">
              {document.type === 'pdf' ? 'Ctrl+/- per zoom • ' : 'R per ruotare • '}
              Ctrl+D per download
            </span>
            <span className="text-sm text-gray-500">
              ESC per chiudere
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
});

FullscreenDocumentModal.displayName = 'FullscreenDocumentModal';

export default FullscreenDocumentModal;